import {
  Accordion, ActionIcon, Alert, Anchor, Badge, Button, Card, FileButton, Grid, Group, List, Modal, NumberInput, Paper, Radio, Select,
  Stack, Switch, Table, Tabs, Text, TextInput, Textarea, ThemeIcon, Title, Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArchive, IconArrowDown, IconArrowLeft, IconArrowUp, IconCheck, IconEye, IconFileUpload, IconPencil, IconPhoto, IconPlus,
  IconSend, IconTrash, IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { fmtDate, fmtDateTime } from '../../lib/format';
import type { CourseVersion, VLesson, VQuestion } from '../../lib/types';
import { VideoBlock } from '../../ui/LessonMedia';
import { Markdown } from '../../ui/Markdown';
import { QueryState } from '../../ui/QueryState';
import { VERSION_STATUS } from './ContentCourses';

const filePath = (id: number) => `/admin/content/files/${id}`;

function useVersionMutation<T>(versionId: number, fn: (v: T) => Promise<unknown>, success?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['version', versionId] });
      await qc.invalidateQueries({ queryKey: ['content-courses'] });
      if (success) notifications.show({ color: 'teal', message: success });
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });
}

async function uploadFile(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return api.upload<{ id: number; filename: string; mime: string }>('/admin/content/files', fd);
}

function SettingsTab({ v, ro }: { v: CourseVersion; ro: boolean }) {
  const [f, setF] = useState({ title: v.title, description: v.description, category: v.category, locale: v.locale, durationMin: v.durationMin, passCount: v.passCount, questionsPerAttempt: v.questionsPerAttempt, attemptsLimit: v.attemptsLimit });
  const [auto, setAuto] = useState<number | null>(v.course.autoAssignDays);
  const save = useVersionMutation(v.id, () => api.patch(`/admin/content/versions/${v.id}`, f), 'Сохранено');
  const saveAuto = useVersionMutation(v.id, () => api.patch(`/admin/content/courses/${v.courseId}`, { autoAssignDays: auto }), 'Автоназначение сохранено');
  const set = <K extends keyof typeof f>(k: K, val: (typeof f)[K]) => setF((s) => ({ ...s, [k]: val }));
  return (
    <Grid gap="lg">
      <Grid.Col span={{ base: 12, md: 7 }}>
        <Card withBorder radius="lg" padding="lg">
          <Title order={5} mb="md">Описание</Title>
          <Stack>
            <TextInput label="Название" value={f.title} onChange={(e) => set('title', e.currentTarget.value)} disabled={ro} />
            <Textarea label="Краткое описание" autosize minRows={3} value={f.description} onChange={(e) => set('description', e.currentTarget.value)} disabled={ro} />
            <Group grow>
              <TextInput label="Категория" value={f.category} onChange={(e) => set('category', e.currentTarget.value)} disabled={ro} />
              <Select label="Язык" value={f.locale} onChange={(x) => set('locale', x ?? 'ru')} data={[{ value: 'ru', label: 'Русский' }, { value: 'en', label: 'English' }]} disabled={ro} allowDeselect={false} />
              <NumberInput label="Ориентир, мин" min={1} max={120} value={f.durationMin} onChange={(x) => set('durationMin', Number(x) || 12)} disabled={ro} />
            </Group>
          </Stack>
        </Card>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 5 }}>
        <Stack>
          <Card withBorder radius="lg" padding="lg">
            <Title order={5} mb="md">Правила теста</Title>
            <Stack>
              <NumberInput label="Вопросов в тесте" description="8-10, собираются случайно из пула" min={1} max={10} value={f.questionsPerAttempt} onChange={(x) => set('questionsPerAttempt', Number(x) || 10)} disabled={ro} />
              <NumberInput label="Порог: верных ответов" description={`Сдан, если верно не меньше ${f.passCount} из ${f.questionsPerAttempt}`} min={1} max={10} value={f.passCount} onChange={(x) => set('passCount', Number(x) || 8)} disabled={ro} />
              <NumberInput label="Попыток" min={1} max={10} value={f.attemptsLimit} onChange={(x) => set('attemptsLimit', Number(x) || 3)} disabled={ro} />
            </Stack>
          </Card>
          <Card withBorder radius="lg" padding="lg">
            <Title order={5} mb={4}>Автоназначение новым сотрудникам</Title>
            <Text size="xs" c="dimmed" mb="sm">Настройка курса, а не версии: назначается текущая опубликованная версия при первом входе сотрудника (ASG-02).</Text>
            <Group align="flex-end">
              <Switch label="Включено" checked={auto != null} onChange={(e) => setAuto(e.currentTarget.checked ? 14 : null)} />
              {auto != null && <NumberInput label="Срок, дней" min={1} max={90} w={110} value={auto} onChange={(x) => setAuto(Number(x) || 14)} />}
              <Button size="xs" variant="light" loading={saveAuto.isPending} onClick={() => saveAuto.mutate(undefined)}>Сохранить</Button>
            </Group>
          </Card>
        </Stack>
      </Grid.Col>
      {!ro && <Grid.Col span={12}><Group justify="flex-end"><Button loading={save.isPending} onClick={() => save.mutate(undefined)}>Сохранить</Button></Group></Grid.Col>}
    </Grid>
  );
}

function LessonEditor({ lesson, v, ro, first, last, onMove, hosts }: { lesson: VLesson; v: CourseVersion; ro: boolean; first: boolean; last: boolean; onMove: (d: -1 | 1) => void; hosts: string[] }) {
  const [f, setF] = useState({ title: lesson.title, contentMd: lesson.contentMd, checklist: lesson.checklist.join('\n'), videoUrl: lesson.videoUrl ?? '', isRequired: lesson.isRequired });
  const [file, setFile] = useState(lesson.file);
  const [mode, setMode] = useState<string | null>('edit');
  useEffect(() => setFile(lesson.file), [lesson.file]);
  const save = useVersionMutation(v.id, () => api.patch(`/admin/content/lessons/${lesson.id}`, {
    title: f.title, contentMd: f.contentMd, checklist: f.checklist.split('\n').map((s) => s.trim()).filter(Boolean), videoUrl: f.videoUrl.trim() || null, isRequired: f.isRequired, fileId: file?.id ?? null,
  }), 'Урок сохранён');
  const remove = useVersionMutation(v.id, () => api.del(`/admin/content/lessons/${lesson.id}`), 'Урок удалён');
  const attach = useMutation({ mutationFn: uploadFile, onSuccess: (a) => { setFile(a); notifications.show({ color: 'blue', message: `Файл ${a.filename} загружен. Сохраните урок.` }); }, onError: (e: Error) => notifications.show({ color: 'red', message: e.message }) });
  const image = useMutation({
    mutationFn: uploadFile,
    onSuccess: (a) => setF((cur) => ({ ...cur, contentMd: `${cur.contentMd.trimEnd()}\n\n![${a.filename.replace(/\.[^.]+$/, '')}](attachment:${a.id})\n` })),
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });

  return (
    <Accordion.Item value={String(lesson.id)}>
      <Group wrap="nowrap" gap={4} pr="xs">
        <Accordion.Control>
          <Group gap="xs">
            <Text fw={500}>{lesson.position}. {lesson.title}</Text>
            {!lesson.isRequired && <Badge size="xs" variant="outline" color="gray">необязательный</Badge>}
            {lesson.videoUrl && <Badge size="xs" variant="light">видео</Badge>}
            {lesson.file && <Badge size="xs" variant="light" color="grape">файл</Badge>}
          </Group>
        </Accordion.Control>
        {!ro && (
          <>
            <ActionIcon variant="subtle" color="gray" disabled={first} onClick={() => onMove(-1)} aria-label="Выше"><IconArrowUp size={16} /></ActionIcon>
            <ActionIcon variant="subtle" color="gray" disabled={last} onClick={() => onMove(1)} aria-label="Ниже"><IconArrowDown size={16} /></ActionIcon>
          </>
        )}
      </Group>
      <Accordion.Panel>
        <Stack>
          <TextInput label="Название урока" value={f.title} onChange={(e) => setF({ ...f, title: e.currentTarget.value })} disabled={ro} />
          <Tabs value={mode} onChange={setMode} variant="pills" radius="md">
            <Group justify="space-between" mb="xs">
              <Tabs.List>
                <Tabs.Tab value="edit" leftSection={<IconPencil size={14} />}>Текст (Markdown)</Tabs.Tab>
                <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>Как увидит сотрудник</Tabs.Tab>
              </Tabs.List>
              {!ro && (
                <FileButton onChange={(x) => x && image.mutate(x)} accept="image/png,image/jpeg">
                  {(p) => <Button {...p} size="xs" variant="default" leftSection={<IconPhoto size={14} />} loading={image.isPending}>Вставить картинку</Button>}
                </FileButton>
              )}
            </Group>
            <Tabs.Panel value="edit">
              <Textarea
                autosize minRows={10} maxRows={30} styles={{ input: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } }}
                description={`Markdown без HTML: ###, **жирный**, списки, таблицы, > цитата. Ссылки только на: ${hosts.filter((h) => !h.startsWith('www.') && !h.startsWith('player.')).join(', ')}`}
                value={f.contentMd} onChange={(e) => setF({ ...f, contentMd: e.currentTarget.value })} disabled={ro}
              />
            </Tabs.Panel>
            <Tabs.Panel value="preview"><Paper withBorder p="lg" radius="md"><Markdown attachmentPath={filePath}>{f.contentMd || '_Пусто_'}</Markdown></Paper></Tabs.Panel>
          </Tabs>
          <Textarea label="Что запомнить (чек-лист)" description="3-5 пунктов, каждый с новой строки" autosize minRows={3} value={f.checklist} onChange={(e) => setF({ ...f, checklist: e.currentTarget.value })} disabled={ro} />
          <TextInput label="Видео (ссылка)" description="Только YouTube, Vimeo, Loom, Rutube" placeholder="https://www.youtube.com/watch?v=…" value={f.videoUrl} onChange={(e) => setF({ ...f, videoUrl: e.currentTarget.value })} disabled={ro} />
          <div>
            <Text size="sm" fw={500} mb={4}>Файл урока (PDF, PNG, JPEG до 10 МБ)</Text>
            <Group gap="xs">
              {file ? <Badge size="lg" variant="light" color="grape" rightSection={!ro && <ActionIcon size="xs" variant="transparent" color="grape" onClick={() => setFile(null)}><IconX size={12} /></ActionIcon>}>{file.filename}</Badge> : <Text size="sm" c="dimmed">Нет файла</Text>}
              {!ro && <FileButton onChange={(x) => x && attach.mutate(x)} accept="application/pdf,image/png,image/jpeg">{(p) => <Button {...p} size="xs" variant="default" leftSection={<IconFileUpload size={14} />} loading={attach.isPending}>Загрузить</Button>}</FileButton>}
            </Group>
          </div>
          <Switch label="Обязательный урок (без отметки тест не откроется)" checked={f.isRequired} onChange={(e) => setF({ ...f, isRequired: e.currentTarget.checked })} disabled={ro} />
          {!ro && (
            <Group justify="space-between">
              <Button variant="subtle" color="red" leftSection={<IconTrash size={14} />} loading={remove.isPending} onClick={() => confirm('Удалить урок?') && remove.mutate(undefined)}>Удалить</Button>
              <Button loading={save.isPending} onClick={() => save.mutate(undefined)}>Сохранить урок</Button>
            </Group>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function LessonsTab({ v, ro, hosts }: { v: CourseVersion; ro: boolean; hosts: string[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const add = useVersionMutation(v.id, () => api.post<VLesson>(`/admin/content/versions/${v.id}/lessons`, { title: 'Новый урок' }));
  const reorder = useVersionMutation(v.id, (ids: number[]) => api.post(`/admin/content/versions/${v.id}/lessons/reorder`, { ids }));
  const move = (i: number, d: -1 | 1) => {
    const ids = v.lessons.map((l) => l.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    reorder.mutate(ids);
  };
  return (
    <Stack>
      <Text size="sm" c="dimmed">2-3 небольших урока: основное содержание, 1-3 примера из рабочей практики, «правильно/неправильно», чек-лист из 3-5 пунктов.</Text>
      <Accordion variant="separated" radius="lg" value={open} onChange={setOpen}>
        {v.lessons.map((l, i) => <LessonEditor key={l.id} lesson={l} v={v} ro={ro} first={i === 0} last={i === v.lessons.length - 1} onMove={(d) => move(i, d)} hosts={hosts} />)}
      </Accordion>
      {v.lessons.length === 0 && <Text c="dimmed" size="sm">Уроков пока нет.</Text>}
      {!ro && <Button variant="light" leftSection={<IconPlus size={16} />} style={{ alignSelf: 'flex-start' }} loading={add.isPending} onClick={() => add.mutateAsync(undefined).then((l) => setOpen(String((l as VLesson).id)))}>Добавить урок</Button>}
    </Stack>
  );
}

interface QForm { type: 'SINGLE' | 'TRUE_FALSE'; text: string; explanation: string; category: string; lessonKey: string | null; answers: { text: string; isCorrect: boolean }[] }

function QuestionModal({ q, v, onClose }: { q: VQuestion | null; v: CourseVersion; onClose: () => void }) {
  const [f, setF] = useState<QForm>(() => q
    ? { type: q.type, text: q.text, explanation: q.explanation, category: q.category, lessonKey: q.lessonKey, answers: q.answers.map((a) => ({ text: a.text, isCorrect: a.isCorrect })) }
    : { type: 'SINGLE', text: '', explanation: '', category: '', lessonKey: null, answers: [0, 1, 2, 3].map((i) => ({ text: '', isCorrect: i === 0 })) });
  const save = useVersionMutation(v.id, () => (q ? api.patch(`/admin/content/questions/${q.id}`, f) : api.post(`/admin/content/versions/${v.id}/questions`, f)), 'Вопрос сохранён');
  const correctIdx = f.answers.findIndex((a) => a.isCorrect);
  const setType = (type: QForm['type']) => setF({ ...f, type, answers: type === 'TRUE_FALSE' ? [{ text: 'Верно', isCorrect: true }, { text: 'Неверно', isCorrect: false }] : [0, 1, 2, 3].map((i) => ({ text: f.answers[i]?.text && f.type === 'SINGLE' ? f.answers[i].text : '', isCorrect: i === 0 })) });
  return (
    <Modal opened onClose={onClose} title={q ? `Вопрос ${q.position}` : 'Новый вопрос'} size="lg" centered>
      <Stack>
        <Select label="Тип" value={f.type} onChange={(x) => setType(x as QForm['type'])} data={[{ value: 'SINGLE', label: 'Один верный из 4' }, { value: 'TRUE_FALSE', label: 'Верно / неверно (не больше 30% пула)' }]} allowDeselect={false} />
        <Textarea label="Текст вопроса" autosize minRows={2} value={f.text} onChange={(e) => setF({ ...f, text: e.currentTarget.value })} />
        <div>
          <Text size="sm" fw={500} mb={6}>Варианты (отметьте правильный)</Text>
          <Radio.Group value={String(correctIdx)} onChange={(x) => setF({ ...f, answers: f.answers.map((a, i) => ({ ...a, isCorrect: i === Number(x) })) })}>
            <Stack gap="xs">
              {f.answers.map((a, i) => (
                <Group key={i} wrap="nowrap" gap="xs">
                  <Radio value={String(i)} aria-label="Правильный" />
                  <TextInput style={{ flex: 1 }} value={a.text} readOnly={f.type === 'TRUE_FALSE'} placeholder={`Вариант ${i + 1}`} onChange={(e) => setF({ ...f, answers: f.answers.map((x, j) => (j === i ? { ...x, text: e.currentTarget.value } : x)) })} />
                </Group>
              ))}
            </Stack>
          </Radio.Group>
        </div>
        <Textarea label="Пояснение" description="Показывается в полном разборе после сдачи или последней попытки" autosize minRows={2} value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.currentTarget.value })} />
        <Group grow>
          <TextInput label="Категория (тема)" value={f.category} onChange={(e) => setF({ ...f, category: e.currentTarget.value })} />
          <Select label="Урок по теме" description="Его посоветуют повторить после неуспеха" clearable value={f.lessonKey} onChange={(x) => setF({ ...f, lessonKey: x })} data={v.lessons.map((l) => ({ value: l.key, label: `${l.position}. ${l.title}` }))} />
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} onClick={() => save.mutateAsync(undefined).then(onClose)}>Сохранить</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function QuestionsTab({ v, ro }: { v: CourseVersion; ro: boolean }) {
  const [editing, setEditing] = useState<VQuestion | 'new' | null>(null);
  const remove = useVersionMutation(v.id, (id: number) => api.del(`/admin/content/questions/${id}`), 'Вопрос удалён');
  const lessonTitle = new Map(v.lessons.map((l) => [l.key, `${l.position}. ${l.title}`]));
  const tf = v.questions.filter((q) => q.type === 'TRUE_FALSE').length;
  return (
    <Stack>
      <Group gap="xs">
        <Badge variant="light" color={v.questions.length >= v.questionsPerAttempt * 2 ? 'teal' : 'orange'}>Пул: {v.questions.length} (нужно ≥ {v.questionsPerAttempt * 2})</Badge>
        <Badge variant="light" color={v.questions.length && tf / v.questions.length > 0.3 ? 'orange' : 'teal'}>«Верно/неверно»: {tf} ({v.questions.length ? Math.round((tf / v.questions.length) * 100) : 0}%, ≤ 30%)</Badge>
        <Text size="sm" c="dimmed">В каждую попытку попадает {v.questionsPerAttempt} случайных вопросов, порядок вариантов перемешивается.</Text>
      </Group>
      {v.questions.map((q) => (
        <Card key={q.id} withBorder radius="lg" padding="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={6} style={{ flex: 1 }}>
              <Text fw={600} size="sm">{q.position}. {q.text || <Text span c="red">без текста</Text>}</Text>
              <Group gap={6}>
                <Badge size="xs" variant="light">{q.type === 'TRUE_FALSE' ? 'верно/неверно' : 'один из 4'}</Badge>
                {q.category && <Badge size="xs" variant="outline" color="gray">{q.category}</Badge>}
                {q.lessonKey && <Badge size="xs" variant="light" color="grape">{lessonTitle.get(q.lessonKey) ?? 'урок удалён'}</Badge>}
              </Group>
              <List size="sm" spacing={2} mt={4}>
                {q.answers.map((a) => (
                  <List.Item key={a.id} icon={a.isCorrect ? <ThemeIcon size={16} radius="xl" color="teal"><IconCheck size={11} /></ThemeIcon> : <ThemeIcon size={16} radius="xl" color="gray" variant="light"><IconX size={11} /></ThemeIcon>}>
                    <Text size="sm" c={a.isCorrect ? undefined : 'dimmed'}>{a.text || '—'}</Text>
                  </List.Item>
                ))}
              </List>
            </Stack>
            {!ro && (
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Изменить"><ActionIcon variant="subtle" onClick={() => setEditing(q)}><IconPencil size={16} /></ActionIcon></Tooltip>
                <Tooltip label="Удалить"><ActionIcon variant="subtle" color="red" onClick={() => confirm('Удалить вопрос?') && remove.mutate(q.id)}><IconTrash size={16} /></ActionIcon></Tooltip>
              </Group>
            )}
          </Group>
        </Card>
      ))}
      {!ro && <Button variant="light" leftSection={<IconPlus size={16} />} style={{ alignSelf: 'flex-start' }} onClick={() => setEditing('new')}>Добавить вопрос</Button>}
      {editing && <QuestionModal q={editing === 'new' ? null : editing} v={v} onClose={() => setEditing(null)} />}
    </Stack>
  );
}

export function VersionPreview({ v }: { v: CourseVersion }) {
  return (
    <Accordion variant="separated" radius="lg" multiple>
      {v.lessons.map((l, i) => (
        <Accordion.Item key={l.id} value={String(l.id)}>
          <Accordion.Control><Text fw={500}>Урок {i + 1}. {l.title}</Text></Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {l.videoUrl && <VideoBlock url={l.videoUrl} />}
              <Markdown attachmentPath={filePath}>{l.contentMd}</Markdown>
              {l.checklist.length > 0 && (
                <Paper radius="md" p="md" bg="var(--mantine-color-blue-light)"><Text fw={600} mb="xs">Что запомнить</Text><List size="sm">{l.checklist.map((x, j) => <List.Item key={j}>{x}</List.Item>)}</List></Paper>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
      <Accordion.Item value="pool">
        <Accordion.Control><Text fw={500}>Пул вопросов · {v.questions.length}</Text></Accordion.Control>
        <Accordion.Panel>
          <Stack>
            {v.questions.map((q, i) => (
              <div key={q.id}>
                <Text fw={600} size="sm">{i + 1}. {q.text}</Text>
                <List size="sm">{q.answers.map((a) => <List.Item key={a.id}><Text size="sm" c={a.isCorrect ? 'teal' : undefined} fw={a.isCorrect ? 600 : 400}>{a.text}</Text></List.Item>)}</List>
                {q.explanation && <Text size="xs" c="dimmed">{q.explanation}</Text>}
              </div>
            ))}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function CheckTab({ v, onSubmit, submitting }: { v: CourseVersion; onSubmit: () => void; submitting: boolean }) {
  return (
    <Stack>
      {v.problems.length > 0 ? (
        <Alert color="orange" title="Перед отправкой на одобрение нужно исправить"><List size="sm">{v.problems.map((p) => <List.Item key={p}>{p}</List.Item>)}</List></Alert>
      ) : (
        <Alert color="teal" icon={<IconCheck />}>Версия соответствует правилам. Её проверит и опубликует второй человек с ролью «Контент».</Alert>
      )}
      {v.warnings.length > 0 && <Alert color="yellow" variant="light"><List size="sm">{v.warnings.map((p) => <List.Item key={p}>{p}</List.Item>)}</List></Alert>}
      {v.editable && <Button leftSection={<IconSend size={16} />} disabled={v.problems.length > 0} loading={submitting} onClick={onSubmit} style={{ alignSelf: 'flex-start' }}>Отправить на одобрение</Button>}
      <Text size="sm" c="dimmed">Так курс увидит сотрудник. Правильные ответы отмечены только здесь.</Text>
      <VersionPreview v={v} />
    </Stack>
  );
}

function HistoryTab({ v }: { v: CourseVersion }) {
  return (
    <Card withBorder radius="lg" p={0}>
      <Table verticalSpacing="sm" horizontalSpacing="lg">
        <Table.Thead><Table.Tr><Table.Th>Версия</Table.Th><Table.Th>Статус</Table.Th><Table.Th>Отправлена</Table.Th><Table.Th>Одобрил</Table.Th><Table.Th>Комментарий</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {v.history.map((h) => (
            <Table.Tr key={h.id}>
              <Table.Td><Anchor component={Link} to={`/admin/content/versions/${h.id}`} fw={600}>v{h.version}</Anchor></Table.Td>
              <Table.Td><Badge color={VERSION_STATUS[h.status].color} variant="light">{VERSION_STATUS[h.status].label}</Badge></Table.Td>
              <Table.Td><Text size="sm">{fmtDate(h.submittedAt)}</Text></Table.Td>
              <Table.Td><Text size="sm">{h.approvedByName ? `${h.approvedByName}, ${fmtDate(h.approvedAt)}` : '-'}</Text></Table.Td>
              <Table.Td><Text size="sm" c="dimmed">{h.reviewComment ?? ''}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

export function VersionEditorPage() {
  const versionId = Number(useParams().versionId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<string | null>('settings');
  const [unpubOpen, unpub] = useDisclosure(false);
  const q = useQuery({ queryKey: ['version', versionId], queryFn: () => api.get<CourseVersion>(`/admin/content/versions/${versionId}`) });
  const settings = useQuery({ queryKey: ['content-settings'], queryFn: () => api.get<{ allowedLinkHosts: string[] }>('/admin/content/settings'), staleTime: Infinity });
  const submit = useVersionMutation(versionId, () => api.post(`/admin/content/versions/${versionId}/submit`).catch((e: ApiError) => {
    if (e.problems) setTab('check');
    throw e;
  }), 'Отправлено на одобрение');
  const archive = useVersionMutation(versionId, (archived: boolean) => api.post(`/admin/content/courses/${q.data!.courseId}/${archived ? 'unpublish' : 'restore'}`));
  const newVersion = useMutation({
    mutationFn: () => api.post<{ versionId: number }>(`/admin/content/courses/${q.data!.courseId}/versions`),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['content-courses'] });
      navigate(`/admin/content/versions/${r.versionId}`);
    },
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });

  return (
    <QueryState loading={q.isLoading} error={q.error}>
      {() => {
        const v = q.data!;
        const ro = !v.editable;
        const hosts = settings.data?.allowedLinkHosts ?? [];
        return (
          <Stack>
            <Anchor component={Link} to="/admin/content" size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> Все курсы</Anchor>
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="xs" mb={4}>
                  <Badge color={VERSION_STATUS[v.status].color} variant="light">v{v.version}: {VERSION_STATUS[v.status].label}</Badge>
                  {v.course.archived && <Badge color="dark" variant="light">Курс снят с публикации</Badge>}
                  <Text size="xs" c="dimmed">Автор версии: {v.createdByName}</Text>
                </Group>
                <Title order={2}>{v.title}</Title>
              </div>
              <Group gap="xs">
                {v.course.archived
                  ? <Button variant="default" onClick={() => archive.mutate(false)}>Вернуть в публикацию</Button>
                  : <Button variant="subtle" color="gray" leftSection={<IconArchive size={16} />} onClick={unpub.open}>{v.history.some((h) => h.status === 'PUBLISHED') ? 'Снять с публикации' : 'В архив'}</Button>}
                {(v.status === 'PUBLISHED' || v.status === 'SUPERSEDED') && !v.course.archived && <Button variant="light" loading={newVersion.isPending} onClick={() => newVersion.mutate()}>Создать новую версию</Button>}
                {v.editable && <Button leftSection={<IconSend size={16} />} loading={submit.isPending} onClick={() => submit.mutate(undefined)}>Отправить на одобрение</Button>}
              </Group>
            </Group>
            {v.status === 'RETURNED' && v.reviewComment && <Alert color="orange" title="Возвращено на доработку">{v.reviewComment}</Alert>}
            {v.status === 'ON_APPROVAL' && <Alert color="violet" variant="light">Отправлена {fmtDateTime(v.submittedAt)} ({v.submittedByName}). Одобрить до {fmtDate(v.approvalDueAt)}. Пока версия на одобрении, менять её нельзя.</Alert>}
            {(v.status === 'PUBLISHED' || v.status === 'SUPERSEDED') && <Alert color="gray" variant="light">Опубликована {fmtDate(v.publishedAt)}, одобрил {v.approvedByName}. Опубликованную версию менять нельзя: чтобы внести правки, создайте новую версию.</Alert>}
            <Tabs value={tab} onChange={setTab} keepMounted={false}>
              <Tabs.List mb="md">
                <Tabs.Tab value="settings">Описание и правила</Tabs.Tab>
                <Tabs.Tab value="lessons">Уроки <Badge size="xs" variant="light" ml={4}>{v.lessons.length}</Badge></Tabs.Tab>
                <Tabs.Tab value="questions">Пул вопросов <Badge size="xs" variant="light" ml={4}>{v.questions.length}</Badge></Tabs.Tab>
                <Tabs.Tab value="check">Проверка и предпросмотр {v.problems.length > 0 && <Badge size="xs" color="orange" ml={4}>{v.problems.length}</Badge>}</Tabs.Tab>
                <Tabs.Tab value="history">Версии</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="settings"><SettingsTab key={`${v.id}-${v.status}`} v={v} ro={ro} /></Tabs.Panel>
              <Tabs.Panel value="lessons"><LessonsTab v={v} ro={ro} hosts={hosts} /></Tabs.Panel>
              <Tabs.Panel value="questions"><QuestionsTab v={v} ro={ro} /></Tabs.Panel>
              <Tabs.Panel value="check"><CheckTab v={v} onSubmit={() => submit.mutate(undefined)} submitting={submit.isPending} /></Tabs.Panel>
              <Tabs.Panel value="history"><HistoryTab v={v} /></Tabs.Panel>
            </Tabs>
            <Modal opened={unpubOpen} onClose={unpub.close} title="Снять курс с публикации?" centered>
              <Text size="sm" mb="md">Курс нельзя будет назначать. Выданные назначения можно будет завершить, результаты сохранятся. Вернуть в публикацию можно в любой момент.</Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={unpub.close}>Отмена</Button>
                <Button color="dark" onClick={() => archive.mutateAsync(true).then(unpub.close)}>Снять с публикации</Button>
              </Group>
            </Modal>
          </Stack>
        );
      }}
    </QueryState>
  );
}
